/**
 * Premium testimonials carousel with autoplay, swipe, and directional transitions.
 */
const AUTOPLAY_INTERVAL = 5000;
const TRANSITION_MS = 300;
const SWIPE_THRESHOLD = 48;
const SELECTORS = {
    root: '[data-testimonials-carousel]',
    slide: '[data-testimonials-slide]',
    prev: '[data-testimonials-prev]',
    next: '[data-testimonials-next]',
    track: '[data-testimonials-track]',
};
export function initTestimonials() {
    document.querySelectorAll(SELECTORS.root).forEach((root) => {
        TestimonialsCarousel.create(root);
    });
}
class TestimonialsCarousel {
    constructor(root) {
        this.currentIndex = 0;
        this.timer = 0;
        this.isAnimating = false;
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.touchDeltaX = 0;
        this.root = root;
        this.track = root.querySelector(SELECTORS.track);
        this.slides = Array.from(root.querySelectorAll(SELECTORS.slide));
        this.prevBtn = root.querySelector(SELECTORS.prev);
        this.nextBtn = root.querySelector(SELECTORS.next);
        if (this.slides.length <= 1) {
            return;
        }
        this.bind();
        this.startAutoplay();
    }
    static create(root) {
        new TestimonialsCarousel(root);
    }
    bind() {
        this.prevBtn?.addEventListener('click', () => this.goTo(this.currentIndex - 1, 'prev'));
        this.nextBtn?.addEventListener('click', () => this.goTo(this.currentIndex + 1, 'next'));
        this.root.addEventListener('mouseenter', () => this.stopAutoplay());
        this.root.addEventListener('mouseleave', () => this.startAutoplay());
        this.root.addEventListener('focusin', () => this.stopAutoplay());
        this.root.addEventListener('focusout', (event) => {
            if (!this.root.contains(event.relatedTarget)) {
                this.startAutoplay();
            }
        });
        this.root.addEventListener('keydown', (event) => {
            if (event.key === 'ArrowLeft') {
                event.preventDefault();
                this.goTo(this.currentIndex - 1, 'prev');
            }
            if (event.key === 'ArrowRight') {
                event.preventDefault();
                this.goTo(this.currentIndex + 1, 'next');
            }
        });
        this.track?.addEventListener('touchstart', (event) => this.onTouchStart(event), { passive: true });
        this.track?.addEventListener('touchmove', (event) => this.onTouchMove(event), { passive: false });
        this.track?.addEventListener('touchend', () => this.onTouchEnd());
    }
    onTouchStart(event) {
        const touch = event.changedTouches[0];
        this.touchStartX = touch.clientX;
        this.touchStartY = touch.clientY;
        this.touchDeltaX = 0;
        this.stopAutoplay();
    }
    onTouchMove(event) {
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - this.touchStartX;
        const deltaY = touch.clientY - this.touchStartY;
        if (Math.abs(deltaX) <= Math.abs(deltaY)) {
            return;
        }
        this.touchDeltaX = deltaX;
        if (Math.abs(deltaX) > 10) {
            event.preventDefault();
        }
    }
    onTouchEnd() {
        if (this.touchDeltaX <= -SWIPE_THRESHOLD) {
            this.goTo(this.currentIndex + 1, 'next');
        }
        else if (this.touchDeltaX >= SWIPE_THRESHOLD) {
            this.goTo(this.currentIndex - 1, 'prev');
        }
        this.startAutoplay();
    }
    goTo(index, direction) {
        if (this.isAnimating || this.slides.length <= 1) {
            return;
        }
        const total = this.slides.length;
        const nextIndex = ((index % total) + total) % total;
        if (nextIndex === this.currentIndex) {
            return;
        }
        const resolvedDirection = direction ?? (nextIndex > this.currentIndex || (this.currentIndex === total - 1 && nextIndex === 0) ? 'next' : 'prev');
        const currentSlide = this.slides[this.currentIndex];
        const nextSlide = this.slides[nextIndex];
        this.isAnimating = true;
        this.stopAutoplay();
        const exitClass = resolvedDirection === 'next' ? 'is-exiting-next' : 'is-exiting-prev';
        const enterClass = resolvedDirection === 'next' ? 'is-entering-next' : 'is-entering-prev';
        currentSlide.classList.remove('is-active');
        currentSlide.classList.add(exitClass);
        currentSlide.setAttribute('aria-hidden', 'true');
        nextSlide.classList.add('is-active', enterClass);
        nextSlide.removeAttribute('aria-hidden');
        window.setTimeout(() => {
            currentSlide.classList.remove(exitClass);
            nextSlide.classList.remove(enterClass);
            this.currentIndex = nextIndex;
            this.isAnimating = false;
            this.startAutoplay();
        }, TRANSITION_MS);
    }
    startAutoplay() {
        this.stopAutoplay();
        if (this.slides.length <= 1) {
            return;
        }
        this.timer = window.setInterval(() => {
            this.goTo(this.currentIndex + 1, 'next');
        }, AUTOPLAY_INTERVAL);
    }
    stopAutoplay() {
        window.clearInterval(this.timer);
    }
}
